import React, { useEffect, useState } from "react";
import { getApproverGroups, submitBaselineApprovalSubmission } from "../../services/api";

export default function SubmitForBaselineApprovalModal({
  open,
  entityIds,
  onClose,
  onSubmitted,
  isInlineLight = true,
}) {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [comment, setComment] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const count = Array.isArray(entityIds) ? entityIds.length : 0;

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    setComment("");
    setGroupId("");
    setLoadingGroups(true);
    void getApproverGroups()
      .then((rows) => {
        const active = (rows || []).filter((g) => g.active !== false);
        setGroups(active);
        if (active.length === 1) {
          setGroupId(String(active[0].id || ""));
        }
      })
      .catch((e) => setError(e?.message || "Could not load approver groups."))
      .finally(() => setLoadingGroups(false));
    return undefined;
  }, [open]);

  if (!open) return null;

  const shell = isInlineLight
    ? "rounded-xl border border-slate-200 bg-white shadow-xl max-w-lg w-full"
    : "rounded-xl border border-slate-600 bg-slate-900 shadow-xl max-w-lg w-full";

  const handleSubmit = async () => {
    if (!groupId) {
      setError("Choose an approver group.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await submitBaselineApprovalSubmission({
        entityIds,
        approverGroupId: groupId,
        comment,
      });
      onSubmitted?.(result);
      window.dispatchEvent(new CustomEvent("baselineApprovalQueueChanged"));
      onClose?.();
    } catch (e) {
      setError(e?.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="baseline-submit-title"
    >
      <div className={shell}>
        <div className="px-5 py-4 border-b border-slate-200/80">
          <h2
            id="baseline-submit-title"
            className={
              isInlineLight ? "text-lg font-semibold text-slate-900" : "text-lg font-semibold text-slate-100"
            }
          >
            Submit for approval
          </h2>
          <p className={isInlineLight ? "text-sm text-slate-600 mt-1" : "text-sm text-slate-400 mt-1"}>
            {count} pending item{count === 1 ? "" : "s"} will be sent to the selected approver group.
            Approvers work from the Approvals screen.
          </p>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label
              className={
                isInlineLight
                  ? "block text-xs font-medium text-slate-700 mb-1"
                  : "block text-xs font-medium text-slate-300 mb-1"
              }
            >
              Approver group
            </label>
            <select
              value={groupId}
              disabled={loadingGroups || submitting}
              onChange={(e) => setGroupId(e.target.value)}
              className={
                isInlineLight
                  ? "w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm"
                  : "w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 text-sm"
              }
            >
              <option value="">
                {loadingGroups ? "Loading groups…" : "Select approver group"}
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            {!loadingGroups && groups.length === 0 ? (
              <p className="text-xs text-amber-700 mt-1">
                No approver groups configured. Create one in Admin → AI map building → Review before publish.
              </p>
            ) : null}
          </div>
          <div>
            <label
              className={
                isInlineLight
                  ? "block text-xs font-medium text-slate-700 mb-1"
                  : "block text-xs font-medium text-slate-300 mb-1"
              }
            >
              Comment for approvers
            </label>
            <textarea
              value={comment}
              disabled={submitting}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Why should these be added to the map?"
              className={
                isInlineLight
                  ? "w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm"
                  : "w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-slate-100 text-sm"
              }
            />
          </div>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
        <div className="px-5 py-3 border-t border-slate-200/80 flex justify-end gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={() => onClose?.()}
            className={
              isInlineLight
                ? "px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700"
                : "px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-200"
            }
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || loadingGroups || !groupId || count === 0}
            onClick={() => void handleSubmit()}
            className={
              isInlineLight
                ? "px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium"
                : "px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium"
            }
          >
            {submitting ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      </div>
    </div>
  );
}
